/*
 * Copyright (c) 2021-2022. Revo Digital
 * ---
 * Author: gabriele
 * File: ProjectConfigDialog.tsx
 * Project: mle-client-n
 * Committed last: 2022/1/24 @ 1649
 * ---
 * Description:
 */

import classNames from 'classnames';
import {currentThemeIsDark} from '../../../common/utils';
import {Classes, Dialog, Icon, Tab, Tabs} from '@blueprintjs/core';
import React, {useState} from 'react';
import {ProjectDataForm, ProjectDataFormValues} from '../../forms/ProjectDataForm';
import {useSelector} from 'react-redux';
import {RootState} from '../../../store';
import {Tooltip2} from '@blueprintjs/popover2';
import {sizeOf} from '@revodigital/pamela/lib/common/Size2D';
import {BaseDialogProps, HasEditor} from '../../../common/props';
import {ColorModes} from "../../../common/enums";

export interface ProjectConfigDialogProps extends HasEditor, BaseDialogProps {
}

enum ActiveTabIds {
    GENERAL = 'generale',
    EDITOR = 'editor',
    LABEL = 'etichetta'
}

export const ProjectConfigDialog = (props: ProjectConfigDialogProps) => {
    const authData = useSelector((state: RootState) => state.authState);
    const [activeTabId, setActiveTabId] = useState(ActiveTabIds.GENERAL);

    const handleSubmit = async (values: ProjectDataFormValues) => {
        let prevSelectedShape;
        if (props.editor.hasSelection()) {
            prevSelectedShape = props.editor.getSelectedShape()?.pamelaShape__nonObservable;
            props.editor.deselect();
        }

        props.editor.projectData.title = values.title;
        props.editor.projectData.projectOptions.measureUnit = values.measureUnit;
        props.editor.setLabelSize(sizeOf(values.width, values.height));
        props.editor.saveFeature.encodeAndUpload().then();

        if (prevSelectedShape)
            props.editor.select(prevSelectedShape);

        props.onClose();
    };

    return (
        <Dialog
            canOutsideClickClose={false}
            usePortal
            className={classNames({'bp4-dark': currentThemeIsDark()})}
            style={{width: '40rem'}}
            isOpen={props.isOpen}
            title="Configurazione progetto"
            onClose={props.onClose}
        >
            <div className={Classes.DIALOG_BODY}>
                <Tabs
                    vertical
                    animate
                    selectedTabId={activeTabId}
                    onChange={newTabId => setActiveTabId(newTabId as ActiveTabIds)}
                >
                    <Tab
                        className="outline-none w-full"
                        id={ActiveTabIds.GENERAL}
                        title={<div className="capitalize">
                            <Icon size={12} icon="layout-grid"
                                  className="bp4-text-muted mr-1.5 mb-0.5"/>
                            {ActiveTabIds.GENERAL}
                        </div>}
                        panel={<ProjectDataForm
                            initialValues={{
                                title: props.editor.projectData.title,
                                width: props.editor.pixelToCurrent(props.editor.projectData.labelWidth),
                                height: props.editor.pixelToCurrent(props.editor.projectData.labelHeight),
                                measureUnit: props.editor.projectData.projectOptions.measureUnit,
                                colorProfile: ColorModes.COLORE
                            } as ProjectDataFormValues
                            }
                            projectEditing
                            editor={props.editor}
                            user={authData.user}
                            onSubmit={handleSubmit}
                            showSubmit
                        />}
                    />
                    <Tab
                        className="outline-none w-full"
                        id={ActiveTabIds.EDITOR}
                        title={<Tooltip2 content="Disponibile nei prossimi rilasci">
                            <div className="capitalize">
                                <Icon size={12} icon="cog"
                                      className="bp4-text-muted mr-1.5 mb-0.5"/>
                                {ActiveTabIds.EDITOR}
                            </div>
                        </Tooltip2>}
                        disabled
                    />
                    <Tab
                        className="outline-none w-full"
                        id={ActiveTabIds.LABEL}
                        title={<Tooltip2 content="Disponibile nei prossimi rilasci">
                            <div className="capitalize">
                                <Icon size={12} icon="document"
                                      className="bp4-text-muted mr-1.5 mb-0.5"/>
                                {ActiveTabIds.LABEL}
                            </div>
                        </Tooltip2>}
                        disabled
                    />
                </Tabs>
            </div>
        </Dialog>
    );
};
